import { z } from 'zod';
import { compareInstants, decimalCursorSchema, instantSchema, revisionSchema, semverSchema, sha256Schema, uuidSchema, } from './primitives.js';
export const artifactVersionSchema = z.strictObject({
    version: semverSchema,
    sha256: sha256Schema,
});
export const sourceSnapshotSchema = z.strictObject({
    publisher: z.string().min(1).max(256),
    dataset: z.string().min(1).max(256),
    snapshot_version: z.string().min(1).max(128),
    snapshot_sha256: sha256Schema,
    source_record_id: z.string().min(1).max(512),
});
export const datasetVersionsSchema = z.strictObject({
    catalog: artifactVersionSchema.nullable(),
    graph: artifactVersionSchema.nullable(),
    route_scoring_version: semverSchema,
});
export const tombstoneLifecycleSchema = z
    .strictObject({
    deleted_at: instantSchema,
    restore_until: instantSchema,
    deletion_operation_id: uuidSchema,
    /** Null until the server assigns a globally ordered cursor. */
    deletion_cursor: decimalCursorSchema.nullable(),
    dataset_epoch: uuidSchema,
})
    .superRefine((value, context) => {
    const restoreWindowMs = Date.parse(value.restore_until) - Date.parse(value.deleted_at);
    if (restoreWindowMs !== 30 * 24 * 60 * 60 * 1_000) {
        context.addIssue({
            code: 'custom',
            path: ['restore_until'],
            message: 'restore_until must be exactly 30 days after deleted_at',
        });
    }
});
export const revisionMetadataSchema = z
    .strictObject({
    created_at: instantSchema,
    updated_at: instantSchema,
    revision: revisionSchema,
    tombstone: tombstoneLifecycleSchema.nullable(),
})
    .superRefine((value, context) => {
    if (compareInstants(value.updated_at, value.created_at) < 0) {
        context.addIssue({
            code: 'custom',
            path: ['updated_at'],
            message: 'updated_at must not precede created_at',
        });
    }
    if (value.tombstone && compareInstants(value.tombstone.deleted_at, value.updated_at) !== 0) {
        context.addIssue({
            code: 'custom',
            path: ['tombstone', 'deleted_at'],
            message: "a live tombstone must be the record's latest revision timestamp",
        });
    }
});
/**
 * Monotonic local dataset boundary for the expanded route-draft writer.
 * Absence means that every write must remain readable by the pre-expansion
 * production contract. Once present, it must never be contracted away.
 */
export const EXPANDED_ROUTE_DRAFT_WIRE_GENERATION = 'route_draft.expanded.v1';
export const routeDraftWireGenerationSchema = z.literal(EXPANDED_ROUTE_DRAFT_WIRE_GENERATION);
const datasetEpochBaseShape = {
    dataset_epoch: uuidSchema,
    created_at: instantSchema,
    predecessor_epoch: uuidSchema.nullable(),
    reason: z.enum(['initial', 'authoritative_replace']),
    restore_checkpoint_id: uuidSchema.nullable(),
};
export const datasetEpochSchema = z
    .union([
    z.strictObject(datasetEpochBaseShape),
    z.strictObject({
        ...datasetEpochBaseShape,
        route_draft_wire_generation: routeDraftWireGenerationSchema,
    }),
])
    .superRefine((value, context) => {
    if (value.reason === 'initial' &&
        (value.predecessor_epoch !== null || value.restore_checkpoint_id !== null)) {
        context.addIssue({
            code: 'custom',
            message: 'initial epoch cannot have a predecessor or checkpoint',
        });
    }
    if (value.reason === 'authoritative_replace') {
        if (value.predecessor_epoch === null || value.restore_checkpoint_id === null) {
            context.addIssue({
                code: 'custom',
                message: 'authoritative replacement requires predecessor_epoch and restore_checkpoint_id',
            });
        }
        if (value.predecessor_epoch === value.dataset_epoch) {
            context.addIssue({
                code: 'custom',
                path: ['predecessor_epoch'],
                message: 'replacement must create a new epoch',
            });
        }
    }
});
export function routeDraftWireGenerationOf(epoch) {
    return 'route_draft_wire_generation' in epoch ? epoch.route_draft_wire_generation : null;
}
//# sourceMappingURL=versioning.js.map